import { motion } from "framer-motion";
import Chapter from "./Chapter";

/**
 * The pain — "One control. Twenty frameworks. The same evidence, collected twenty times."
 * Rendered as a stark ledger of duplicated work.
 */
const rows = [
  { label: "Access review",       ctrl: "A.5.15 · CC6.3 · Art.32 · PR.AC-4 · AC-2 · §164.312(a)" },
  { label: "Encryption at rest",  ctrl: "A.8.24 · CC6.7 · Art.32 · PR.DS-1 · SC-28 · §164.312(a)(2)(iv)" },
  { label: "Vendor DPA on file",  ctrl: "A.5.19 · CC9.2 · Art.28 · ID.SC-3 · SA-9 · §164.308(b)(1)" },
  { label: "MFA enforced",        ctrl: "A.8.5  · CC6.6 · Art.32 · PR.AC-7 · IA-2 · 8.4.2" },
  { label: "Backup restore test", ctrl: "A.8.13 · CC7.5 · Art.32 · PR.IP-4 · CP-9 · §164.308(a)(7)" },
];

export default function PainSection() {
  return (
    <section className="relative py-14 md:py-20">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <Chapter number="01" label="The pain" />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <motion.h2
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6, ease: [0.22,1,0.36,1] }}
              className="font-display text-[36px] md:text-[52px] leading-[1.04] tracking-[-0.022em] text-balance"
            >
              One control. Twenty frameworks. The same evidence, collected <span className="grad-text">twenty times</span>.
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.55, delay: 0.1 }}
              className="mt-6 text-ink-muted text-[15px] md:text-[17px] leading-relaxed max-w-[46ch]"
            >
              Auditors ask for the same access review five times, encryption policy eight times, and vendor DPA in every framework's language.
              The work is the same. The forms are different. The clock burns.
            </motion.p>
          </div>

          <div className="lg:col-span-7">
            <div className="machined p-1 md:p-2 relative">
              <div className="px-5 py-3 border-b border-white/8 flex items-center justify-between">
                <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-ink-muted">Redundant evidence ledger</span>
                <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-ink-muted">Before NormShift</span>
              </div>
              <ul className="divide-y divide-white/6">
                {rows.map((r, i) => (
                  <motion.li
                    key={r.label}
                    initial={{ opacity: 0, x: -12 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.45, delay: i * 0.06, ease: [0.22,1,0.36,1] }}
                    className="px-5 py-4 grid grid-cols-12 gap-x-3 gap-y-1.5 items-baseline md:items-center"
                  >
                    <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-ink-muted col-span-2 md:col-span-1 tabular">×20</span>
                    <span className="font-display text-[15px] md:text-[16px] col-span-10 md:col-span-4">{r.label}</span>
                    <span className="col-span-12 md:col-span-7 font-mono text-[11px] text-white/45 break-words md:truncate">{r.ctrl}</span>
                  </motion.li>
                ))}
              </ul>
              <div className="px-5 py-4 border-t border-white/8 flex items-center justify-between font-mono text-[11px] text-ink-muted">
                <span className="tracking-[0.22em] uppercase">Total effort</span>
                <span className="tabular text-white/80"><span className="grad-text">100+</span> collections</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
